import React from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import { Box, Typography } from "@mui/material";
import { Accordion, DataTable, IconButton } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import {
  AirplaneTakeOff01Icon,
  Car01Icon,
  ClipboardIcon,
  Hotel01Icon,
  Train01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { GET_TRANSACTION_TRAVEL_DATA } from "../../../graphql/travelData";
import { useCopyToClipboard } from "components/common/useCopyToClipboard";
import TransactionsType from "enums/TransactionsType";
import TravelIndustry from "enums/TravelIndustry";
import {
  buildAirlineRows,
  buildAirlineTotalRows,
  buildAncillaryFeeRows,
  buildCarRentalRows,
  buildIndustryFacts,
  buildLodgingRows,
  buildRailRows,
  buildSegmentRows,
  industryHasDetails,
  INDUSTRY_TITLE,
  presentIndustries,
  type TravelData,
  type TravelFact,
  type TravelRow,
  type TravelSegmentRow,
} from "utils/transactions/travelDataFormat";

const INDUSTRY_ICON: Record<TravelIndustry, IconSvgElement> = {
  airline: AirplaneTakeOff01Icon,
  lodging: Hotel01Icon,
  carRental: Car01Icon,
  rail: Train01Icon,
};

interface TravelDetailsProps {
  travelData: TravelData | null | undefined;
  industries: TravelIndustry[];
}

/**
 * The travel industries a transaction reported, and the data behind them. The
 * section strip needs the count before it can offer the tab, so the query is a
 * hook rather than a guard inside the panel.
 */
export function useTravelIndustries(
  transactionId: string | null,
  transactionType?: string,
) {
  const { t } = useTranslation();

  const typeApplies = transactionType === TransactionsType.CAPTURE;

  const { data } = useQuery(GET_TRANSACTION_TRAVEL_DATA, {
    variables: { id: transactionId },
    skip: !transactionId || !typeApplies,
    fetchPolicy: "cache-and-network",
  });

  const travelData = data?.transaction?.travelData as
    | TravelData
    | null
    | undefined;

  return { travelData, industries: presentIndustries(t, travelData) };
}

/**
 * Admin view of the Level 3 travel data a merchant reported for a card purchase. Mirrors the
 * employee drawer's sections using the admin accordion and card styling.
 */
const TravelDetails: React.FC<TravelDetailsProps> = ({
  travelData,
  industries,
}) => {
  const { t } = useTranslation();

  if (!industries.length) return null;

  return (
    <Box
      sx={{
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {industries.map((industry) => {
        const facts = buildIndustryFacts(t, industry, travelData);

        // An industry with nothing to expand gets a flat row instead of an
        // accordion that opens onto nothing.
        return industryHasDetails(t, industry, travelData) ? (
          <Accordion
            key={industry}
            title={t(INDUSTRY_TITLE[industry])}
            description={<KeyFacts facts={facts} />}
            icon={
              <HugeiconsIcon
                icon={INDUSTRY_ICON[industry]}
                size={16}
                color="var(--color-text-brand)"
              />
            }
            defaultExpanded={industries.length === 1}
          >
            {renderIndustry(industry, travelData as TravelData, t)}
          </Accordion>
        ) : (
          <Box
            key={industry}
            sx={{
              display: "flex",
              flexDirection: "column",
              paddingX: "var(--space-5)",
              paddingY: "var(--space-4)",
            }}
          >
            <Typography variant="body1">
              {t(INDUSTRY_TITLE[industry])}
            </Typography>
            <KeyFacts facts={facts} />
          </Box>
        );
      })}
    </Box>
  );
};

const KeyFacts: React.FC<{ facts: TravelFact[] }> = ({ facts }) => {
  const { t } = useTranslation();
  const copy = useCopyToClipboard(t("Copied to clipboard"));

  if (!facts.length) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        columnGap: "var(--space-7)",
        rowGap: "var(--space-3)",
        paddingTop: "var(--space-2)",
        paddingBottom: "var(--space-3)",
      }}
    >
      {facts.map((fact) => (
        <Box sx={{ display: "flex", flexDirection: "column" }} key={fact.label}>
          <Typography
            variant="caption"
            component="span"
            sx={{ color: "var(--color-text-secondary)" }}
          >
            {fact.label}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-2)",
            }}
          >
            <Typography variant="label" component="span">
              {fact.value}
            </Typography>
            <IconButton
              variant="ghost"
              size="small"
              aria-label={t("Copy")}
              onClick={(event) => {
                event.stopPropagation();
                copy(fact.value);
              }}
            >
              <HugeiconsIcon
                icon={ClipboardIcon}
                size={14}
                color="var(--color-text-brand)"
              />
            </IconButton>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const DetailRows: React.FC<{ rows: TravelRow[] }> = ({ rows }) => {
  if (!rows.length) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      {rows.map((row) => (
        <Box
          key={row.key}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            paddingY: "var(--space-2)",
          }}
        >
          <Typography
            variant="body2"
            component="span"
            sx={{ color: "var(--color-text-secondary)" }}
          >
            {row.label}
          </Typography>
          <Typography
            variant="body2"
            component="span"
            sx={{ textAlign: "right" }}
          >
            {row.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const Subsection: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      width: "100%",
      paddingTop: "var(--space-5)",
    }}
  >
    <Typography
      variant="label"
      component="span"
      sx={{ paddingBottom: "var(--space-2)" }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const SegmentTable: React.FC<{ segments: TravelSegmentRow[] }> = ({
  segments,
}) => {
  const { t } = useTranslation();

  const columns: DataColumn<TravelSegmentRow>[] = [
    { id: "date", header: t("Date"), cell: (row) => row.date || "-" },
    { id: "flight", header: t("Flight"), cell: (row) => row.flight || "-" },
    { id: "route", header: t("Route"), cell: (row) => row.route || "-" },
    {
      id: "fareBasisCode",
      header: t("Fare Basis"),
      cell: (row) => row.fareBasisCode || "-",
    },
    { id: "fare", header: t("Fare"), cell: (row) => row.fare || "-" },
  ];

  return (
    <DataTable<TravelSegmentRow>
      columns={columns}
      rows={segments}
      getRowKey={(row) => row.key}
      stickyHeader
    />
  );
};

function renderIndustry(
  industry: TravelIndustry,
  travelData: TravelData,
  t: ReturnType<typeof useTranslation>["t"],
): JSX.Element {
  switch (industry) {
    case TravelIndustry.AIRLINE: {
      const airline = travelData.airline ?? {};
      const segments = buildSegmentRows(t, airline);
      const fees = buildAncillaryFeeRows(t, airline);

      return (
        <>
          <DetailRows rows={buildAirlineRows(t, airline)} />
          {fees.length > 0 && (
            <Subsection title={t("Ancillary Fees")}>
              <DetailRows rows={fees} />
            </Subsection>
          )}
          <Box sx={{ paddingTop: "var(--space-5)" }}>
            <DetailRows rows={buildAirlineTotalRows(t, airline)} />
          </Box>
          {segments.length > 0 && (
            <Subsection title={t("Itinerary")}>
              <SegmentTable segments={segments} />
            </Subsection>
          )}
        </>
      );
    }
    case TravelIndustry.LODGING: {
      const lodging = travelData.lodging ?? {};

      return <DetailRows rows={buildLodgingRows(t, lodging)} />;
    }
    case TravelIndustry.CAR_RENTAL: {
      const carRental = travelData.carRental ?? {};

      return <DetailRows rows={buildCarRentalRows(t, carRental)} />;
    }
    default: {
      const rail = travelData.rail ?? {};

      return <DetailRows rows={buildRailRows(t, rail)} />;
    }
  }
}

export default TravelDetails;
